/**
 * InputModeToggle Component
 * 
 * Toggle button to cycle between three input modes:
 * - PTT (push-to-talk): green, hand/mic icon — default
 * - Keyboard: purple, keyboard icon
 * - Voice Auto (VAD): blue, mic+auto icon
 * 
 * Cycle: PTT -> Keyboard -> Voice Auto -> PTT
 * Works on both mobile and desktop.
 */

import { useTranslation } from 'react-i18next';
import { MicrophoneIcon } from '@heroicons/react/24/solid';

type InputMode = 'ptt' | 'keyboard' | 'voice';

// Cycle order: PTT -> Keyboard -> Voice Auto -> PTT
const MODE_CYCLE: Record<InputMode, InputMode> = {
  ptt: 'keyboard',
  keyboard: 'voice',
  voice: 'ptt',
};

interface InputModeToggleProps {
  currentMode: InputMode;
  onToggle: (mode: InputMode) => void;
  disabled?: boolean;
  variant?: 'desktop' | 'mobile';
}

export default function InputModeToggle({ 
  currentMode, 
  onToggle, 
  disabled = false,
  variant = 'desktop' 
}: InputModeToggleProps) {
  const { t } = useTranslation();
  
  const handleToggle = () => {
    if (disabled) return;
    const nextMode = MODE_CYCLE[currentMode] || 'ptt';
    onToggle(nextMode);
  };

  // Mobile: smaller, compact design for header
  // Desktop: larger, more prominent
  const buttonSize = variant === 'mobile' 
    ? 'w-10 h-10' 
    : 'w-16 h-16 md:w-20 md:h-20';
  
  const iconSize = variant === 'mobile' 
    ? 'w-5 h-5' 
    : 'w-8 h-8 md:w-10 md:h-10';

  // Colors per mode
  const bgColor = disabled
    ? 'bg-gray-400 cursor-not-allowed'
    : currentMode === 'ptt'
      ? 'bg-green-600 hover:bg-green-700 active:bg-green-800'
      : currentMode === 'keyboard'
        ? 'bg-purple-600 hover:bg-purple-700 active:bg-purple-800'
        : 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800'; // voice (VAD)

  // Tooltip: shows what clicking will switch TO
  const nextMode = MODE_CYCLE[currentMode] || 'ptt';
  const titleMap: Record<InputMode, string> = {
    ptt: t('scenario.session.keyboard.switchToPTT', 'Switch to Push-to-Talk'),
    keyboard: t('scenario.session.keyboard.switchToKeyboard', 'Switch to Keyboard'),
    voice: t('scenario.session.keyboard.switchToVoice', 'Switch to Voice Auto'),
  };
  const title = titleMap[nextMode];

  return (
    <button
      onClick={handleToggle}
      disabled={disabled}
      className={`${buttonSize} rounded-full shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-110 active:scale-95 flex items-center justify-center ${bgColor}`}
      title={title}
      aria-label={title}
    >
      {currentMode === 'ptt' ? (
        // PTT mode: show keyboard icon (clicking switches TO keyboard)
        <svg 
          className={`${iconSize} text-white`} 
          fill="none" 
          viewBox="0 0 24 24" 
          stroke="currentColor"
        >
          <rect x="2" y="5" width="20" height="14" rx="2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <line x1="6" y1="9" x2="6.01" y2="9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <line x1="10" y1="9" x2="10.01" y2="9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <line x1="14" y1="9" x2="14.01" y2="9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <line x1="18" y1="9" x2="18.01" y2="9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <line x1="8" y1="13" x2="16" y2="13" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ) : currentMode === 'keyboard' ? (
        // Keyboard mode: show mic+auto icon (clicking switches TO voice auto)
        <div className="relative flex items-center justify-center">
          <MicrophoneIcon className={`${iconSize} text-white`} />
          <span className="absolute -bottom-0.5 -right-0.5 text-[8px] font-bold text-white bg-blue-500 rounded px-0.5 leading-tight">A</span>
        </div>
      ) : (
        // Voice auto mode: show PTT icon (clicking switches TO PTT)
        <svg
          className={`${iconSize} text-white`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth="2"
        >
          {/* Hand/finger pressing icon for PTT */}
          <path d="M12 18.5V22M8 22h8" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M12 2a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M19 10v1a7 7 0 0 1-14 0v-1" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </button>
  );
}
